import { SelectOptions } from '../../../../../React/Common/Components/Selects'

export type Validation = {
    required?: boolean
    encryptedRequired?: boolean
    pattern?: string
    maxLength?: number
    minLength?: number
    maxWordLength?: number
    uniqueItems?: boolean
    contentMediaType?: string
}

export enum FormTypes {
    BOOLEAN = 'boolean',
    INTEGER = 'integer',
    NUMBER = 'number',
    STRING = 'string',
    SELECT = 'select',
    ARRAY = 'array',
    PASSWORD = 'password',
    MULTILINE = 'multi-line',
    UNKNOWN = 'unknown'
}

export type defaultTypes = Definable<string | boolean | number | string[] | { value: string }[]>

export interface ConfigurableTableConfigBase {
    name: string
    label?: Record<string, string>
    type: FormTypes
    default?: defaultTypes
    telsysTypes?: string[]
    validations?: Validation
}

export interface StringConfigurableTableConfig extends ConfigurableTableConfigBase {
    name: string
    label?: Record<string, string>
    type: FormTypes.STRING
    default?: string
    telsysTypes?: string[]
    validations?: Validation
}

export interface BooleanConfigurableTableConfig extends ConfigurableTableConfigBase {
    name: string
    label?: Record<string, string>
    type: FormTypes.BOOLEAN
    default?: boolean
    telsysTypes?: string[]
    validations?: Validation
}

export interface IntegerConfigurableTableConfig extends ConfigurableTableConfigBase {
    name: string
    label?: Record<string, string>
    type: FormTypes.INTEGER | FormTypes.NUMBER
    default?: number
    telsysTypes?: string[]
    validations?: Validation
}

export interface SelectConfigurableTableConfig extends ConfigurableTableConfigBase {
    name: string
    label?: Record<string, string>
    type: FormTypes.SELECT
    options: SelectOptions<string>
    default?: string
    telsysTypes?: string[]
    validations?: Validation
}

export interface ArrayConfigurableTableConfig extends ConfigurableTableConfigBase {
    name: string
    label?: Record<string, string>
    type: FormTypes.ARRAY
    arrayFieldName: string
    default?: { value: string }[]
    telsysTypes?: string[]
    validations?: Validation
}

export interface PasswordConfigurableTableConfig extends ConfigurableTableConfigBase {
    name: string
    label?: Record<string, string>
    type: FormTypes.PASSWORD
    default?: string
    telsysTypes?: string[]
    validations?: Validation
}

export interface MultiLineConfigurableTableConfig extends ConfigurableTableConfigBase {
    name: string
    label?: Record<string, string>
    type: FormTypes.MULTILINE
    default?: string
    telsysTypes?: string[]
    validations?: Validation
}

/**
 * Type representing all possible configurations for a configurable table field.
 *
 * @type 'string' - Represent a string field configuration.
 *                  This is a standard text input.
 * @type 'boolean' - Represent a boolean field configuration.
 *                   This is a checkbox input.
 * @type 'integer' - Represent an integer field configuration.
 *                   This is a number input.
 * @type 'select' - Represent a select field configuration with options.
 *                  This is a dropdown select input.
 * @type 'array' - Represent an array field configuration with items.
 *                  This is a field that can hold multiple values, typically rendered as a list of inputs.
 * @type 'password' - Represents a password field configuration
 *                  This field will use the password text input which allows of text obfuscation
 * @type 'multi-line' - Represents a text areas input field configuration
 *                  This allows for large amounts of text to be displayed
 *
 */
export type ConfigurableTableConfig =
    | SelectConfigurableTableConfig
    | StringConfigurableTableConfig
    | BooleanConfigurableTableConfig
    | IntegerConfigurableTableConfig
    | ArrayConfigurableTableConfig
    | PasswordConfigurableTableConfig
    | MultiLineConfigurableTableConfig
